import React from "react";
import AppModal from "../AppModal";
import { resolveMediaUrl } from "../../helpers/resolveMediaUrl";

const imageTypes = ["jpg", "jpeg", "png", "gif", "webp"];
const videoTypes = ["mp4", "webm"];
const audioTypes = ["mp3", "wav", "ogg"];

function getExtension(url = "") {
	return url.split(".").pop()?.toLowerCase() || "";
}

function getFileName(url = "") {
	return url.split("/").pop() || url;
}

function IconVideo() {
	return (
		<svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polygon points="23 7 16 12 23 17 23 7" />
			<rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
		</svg>
	);
}

function IconAudio() {
	return (
		<svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
			<path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
			<path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
		</svg>
	);
}

function IconFile() {
	return (
		<svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
			<path d="M14 2v6h6" />
		</svg>
	);
}

function IconPlus() {
	return (
		<svg className="size-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
			<path d="M12 5v14M5 12h14" />
		</svg>
	);
}

function MediaPreview({ media }) {
	const src = resolveMediaUrl(media.url);
	const extension = getExtension(src || media.url);
	const fileName = getFileName(src || media.url);

	if (imageTypes.includes(extension)) {
		return (
			<a
				href={src}
				target="_blank"
				rel="noreferrer"
				className="block aspect-video overflow-hidden rounded-xl bg-default-100 dark:bg-default-800"
			>
				<img src={src} alt={fileName} className="h-full w-full object-cover" />
			</a>
		);
	}

	if (videoTypes.includes(extension)) {
		return (
			<a
				href={src}
				target="_blank"
				rel="noreferrer"
				className="flex aspect-video items-center justify-center rounded-xl bg-sky-50 text-sky-600 dark:bg-sky-500/10 dark:text-sky-200"
			>
				<IconVideo />
			</a>
		);
	}

	if (audioTypes.includes(extension)) {
		return (
			<a
				href={src}
				target="_blank"
				rel="noreferrer"
				className="flex aspect-video items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-200"
			>
				<IconAudio />
			</a>
		);
	}

	return (
		<a
			href={src}
			target="_blank"
			rel="noreferrer"
			className="flex aspect-video items-center justify-center rounded-xl bg-default-100 text-default-600 dark:bg-default-800 dark:text-default-200"
		>
			<IconFile />
		</a>
	);
}

const MediasModal = ({
	open,
	onClose,
	medias,
	selectedNode,
	handleAddNodeUrl,
	handleDeleteNodeUrl,
}) => {
	return (
		<AppModal
			open={open}
			onClose={onClose}
			title={`Mídias do nó ${selectedNode}`}
			subtitle="Anexe arquivos e remova mídias já vinculadas a este nó."
			maxWidth={860}
			footer={
				<button type="button" className="btn btn-sub-gray" onClick={onClose}>
					Fechar
				</button>
			}
		>
			<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
				{(medias || []).map(media => (
					<div
						key={media.id}
						className="rounded-2xl border border-default-200 bg-default-50 p-3 dark:border-default-700 dark:bg-default-900"
					>
						<div className="relative">
							<MediaPreview media={media} />
							<button
								type="button"
								className="absolute top-2 right-2 inline-flex size-7 items-center justify-center rounded-full bg-danger text-white shadow-sm transition hover:opacity-90"
								onClick={() => handleDeleteNodeUrl(selectedNode, media.id)}
								aria-label="Remover mídia"
							>
								x
							</button>
						</div>
						<p className="mt-3 mb-0 truncate text-sm text-default-700 dark:text-default-200">
							{getFileName(media.url)}
						</p>
					</div>
				))}

				<label className="flex min-h-[12rem] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-default-300 bg-default-50 p-4 text-center text-default-500 transition hover:border-primary hover:text-primary dark:border-default-700 dark:bg-default-900 dark:text-default-400">
					<input
						type="file"
						className="hidden"
						onChange={event => {
							handleAddNodeUrl(event, selectedNode);
							event.target.value = "";
						}}
					/>
					<IconPlus />
					<div>
						<p className="mb-1 text-sm font-semibold">Adicionar mídia</p>
						<p className="m-0 text-xs">Imagem, vídeo, áudio ou arquivo</p>
					</div>
				</label>
			</div>
		</AppModal>
	);
};

export default MediasModal;
